"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _get = _interopRequireDefault(require("lodash/get"));
var _cloneDeep = _interopRequireDefault(require("lodash/cloneDeep"));
var _reactFinalForm = require("react-final-form");
var _components = require("@folio/stripes/components");
var _hooks = require("../hooks");
var _ActionListFieldArray = _interopRequireDefault(require("../../../styles/ActionListFieldArray.css"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const EDITING_ACTIONS_WIDTH = 25;
const NON_EDITING_ACTIONS_WIDTH = 20;
const TOTAL_WIDTH = 100;
const propTypes = {
  actionAssigner: _propTypes.default.func,
  columnMapping: _propTypes.default.object,
  creatableFields: _propTypes.default.object,
  createCallback: _propTypes.default.func,
  defaultNewObject: _propTypes.default.object,
  editableFields: _propTypes.default.object,
  fields: _propTypes.default.object,
  fieldComponents: _propTypes.default.object,
  formatter: _propTypes.default.object,
  hideActionsColumn: _propTypes.default.bool,
  hideCreateButton: _propTypes.default.bool,
  intlKey: _propTypes.default.string,
  intlNS: _propTypes.default.string,
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.node]),
  labelOverrides: _propTypes.default.object,
  onRowClick: _propTypes.default.func,
  triggerFormSubmit: _propTypes.default.func.isRequired,
  validateFields: _propTypes.default.object,
  visibleFields: _propTypes.default.arrayOf(_propTypes.default.string)
};

// This needs to be outside of the main component for forwardRef to work properly it seems.
const ActionTrigger = /*#__PURE__*/(0, _react.forwardRef)((_ref, ref) => {
  let {
    action,
    ...actionTriggerProps
  } = _ref;
  if (action.icon) {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.IconButton, {
      ref: ref,
      icon: action.icon,
      ...actionTriggerProps
    });
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
    ref: ref,
    marginBottom0: true,
    ...actionTriggerProps,
    children: action.label ?? action.name
  });
});
ActionTrigger.propTypes = {
  action: _propTypes.default.shape({
    icon: _propTypes.default.string,
    label: _propTypes.default.string,
    name: _propTypes.default.string
  })
};
const NEW_ROW = 'NEW_ROW';
const ActionListFieldArray = /*#__PURE__*/(0, _react.forwardRef)((_ref2, ref) => {
  let {
    actionAssigner,
    columnMapping,
    creatableFields,
    createCallback,
    defaultNewObject,
    editableFields,
    fields,
    fieldComponents,
    hideActionsColumn = false,
    hideCreateButton = false,
    intlKey: passedIntlKey,
    intlNS: passedIntlNS,
    label,
    labelOverrides = {},
    onRowClick,
    validateFields,
    visibleFields,
    triggerFormSubmit,
    ...mclProps // Assume anything left over is to directly apply to the MCL
  } = _ref2;
  // Grab finalForm functions/values from form hooks
  const {
    change
  } = (0, _reactFinalForm.useForm)();
  const {
    hasValidationErrors,
    initialValues,
    pristine,
    submitting,
    values
  } = (0, _reactFinalForm.useFormState)();
  const kintIntl = (0, _hooks.useKintIntl)(passedIntlKey, passedIntlNS);
  /*
    Keep track of which field we are editing.
    null for no field, string id if we are editing an existing field and
    'NEW_ROW' for a new row
  */
  const [editing, setEditing] = (0, _react.useState)((fields?.value?.filter(f => f._isNewActionListRow)?.length ?? 0) > 0 ? NEW_ROW : undefined);
  const toggleEditing = (0, _react.useCallback)(id => {
    if (editing) {
      setEditing();
    } else {
      setEditing(id);
    }
  }, [editing]);

  // Ensure editing doesn't get stuck in "NEW_ROW" state;
  (0, _react.useEffect)(() => {
    if (editing === NEW_ROW && (fields?.value?.filter(f => f._isNewActionListRow)?.length ?? 0) === 0) {
      setEditing();
    }
  }, [editing, fields?.value]);
  const handleSave = index => {
    const {
      actionListActions: _a,
      fieldName: _fn,
      fieldIndex: _fi,
      _isNewActionListRow: _inalr,
      ...rowData
    } = fields.value[index];

    // Find "edit" entry in actionAssigner
    const editCallback = actionAssigner(rowData)?.find(act => act.name === 'edit')?.callback;
    if (editCallback) {
      editCallback(rowData);
    }
  };
  const handleCreate = index => {
    const {
      actionListActions: _a,
      fieldName: _fn,
      fieldIndex: _fi,
      _isNewActionListRow: _inalr,
      ...rowData
    } = fields.value[index];
    if (createCallback) {
      createCallback(rowData);
    }
  };
  const handleClickCreate = (0, _react.useCallback)(() => {
    toggleEditing(NEW_ROW);
    fields.unshift({
      ...defaultNewObject,
      _isNewActionListRow: true
    });
  }, [defaultNewObject, fields, toggleEditing]);

  // Way to go into create mode from external component, and way to tell internal editing state
  (0, _react.useImperativeHandle)(ref, () => ({
    create: handleClickCreate,
    editing
  }), [editing, handleClickCreate]);
  const getColumnWidths = () => {
    const widthNotInUseByActions = editing ? TOTAL_WIDTH - EDITING_ACTIONS_WIDTH : TOTAL_WIDTH - NON_EDITING_ACTIONS_WIDTH;
    const staticWidth = widthNotInUseByActions / visibleFields.length;
    const widthsObject = {};
    visibleFields.forEach(f => {
      if (f !== 'actionListActions') {
        widthsObject[f] = `${staticWidth}%`;
      }
    });
    widthsObject.actionListActions = editing ? `${EDITING_ACTIONS_WIDTH}%` : `${NON_EDITING_ACTIONS_WIDTH}%`;
    return widthsObject;
  };
  const renderActionButtons = data => {
    const fieldName = `contentData[${data.rowIndex}]`;
    const {
      actionListActions: actions,
      ...rest
    } = data;
    if (data.id === editing || !data.id && editing === NEW_ROW) {
      // Render the save/cancel buttons
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
        id: `action-button-parent-${data.rowIndex + 1}`,
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
          buttonStyle: "primary",
          disabled: hasValidationErrors || submitting || pristine,
          marginBottom0: true,
          onClick: () => {
            triggerFormSubmit(); // This is set up as () => null in ActionList, so essentially only acts here to force validation

            if (!hasValidationErrors) {
              if (!data.id && editing === NEW_ROW) {
                handleCreate(data.rowIndex);
              } else {
                handleSave(data.rowIndex);
              }
              toggleEditing();
            }
          },
          type: "submit",
          children: kintIntl.formatKintMessage({
            id: 'save',
            overrideValue: labelOverrides?.save
          })
        }, `save[${data.rowIndex}]`), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
          "data-type-button": "cancel",
          marginBottom0: true,
          onClick: () => {
            if (!data.id && editing === NEW_ROW) {
              fields.remove(data.rowIndex);
              toggleEditing(NEW_ROW);
            } else {
              change(fieldName, (0, _get.default)(initialValues, fieldName));
              toggleEditing(data.id);
            }
          },
          children: kintIntl.formatKintMessage({
            id: 'cancel',
            overrideValue: labelOverrides?.cancel
          })
        }, `cancel[${data.rowIndex}]`)]
      });
    }
    return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      id: `action-button-parent-${data.rowIndex + 1}`,
      children: actions?.map(action => {
        let actionFunction;
        if (action.callback) {
          actionFunction = () => action.callback(rest);
        }

        // Edit has special action functionality, revealing fields etc.
        if (action.name === 'edit') {
          actionFunction = () => toggleEditing(data.id);
        }
        let ariaLabel = `action-${action.name}[${data.rowIndex}]`;
        if (action?.ariaLabel) {
          if (typeof action.ariaLabel === 'function') {
            ariaLabel = action.ariaLabel(data);
          } else if (typeof action.ariaLabel === 'string') {
            ariaLabel = action.ariaLabel;
          } else {
            throw new Error(`Provided ariaLabel for action "${action.name}" must be a function or a string.`);
          }
        }
        let tooltip;
        let tooltipSub;
        if (action?.tooltip) {
          if (typeof action.tooltip === 'function') {
            tooltip = action.tooltip(data);
          } else {
            tooltip = action.tooltip;
          }
          if (action?.tooltipSub) {
            if (typeof action.tooltipSub === 'function') {
              tooltipSub = action.tooltipSub(data);
            } else {
              tooltipSub = action.tooltipSub;
            }
          }
        }

        // If a tooltip is declared, render that around the actionButton
        if (tooltip) {
          return /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Tooltip, {
            id: `action-${action.name}[${data.rowIndex}]-tooltip`,
            sub: tooltipSub,
            text: tooltip,
            children: _ref3 => {
              let {
                ref: actionTriggerRef,
                ariaIds
              } = _ref3;
              return /*#__PURE__*/(0, _jsxRuntime.jsx)(ActionTrigger, {
                ref: actionTriggerRef,
                action: action,
                "aria-describedby": ariaIds.sub,
                "aria-labelledby": ariaIds.text,
                disabled: !!editing,
                onClick: e => {
                  e.stopPropagation();
                  if (actionFunction) {
                    return actionFunction();
                  }
                  return null;
                },
                to: action.to
              }, `action-${action.name}[${data.rowIndex}]`);
            }
          });
        }

        // Finally, render the action button itself
        return /*#__PURE__*/(0, _jsxRuntime.jsx)(ActionTrigger, {
          action: action,
          ariaLabel: ariaLabel,
          disabled: !!editing,
          onClick: e => {
            e.stopPropagation();
            if (actionFunction) {
              return actionFunction();
            }
            return null;
          },
          to: action.to
        }, `action-${action.name}[${data.rowIndex}]`);
      })
    });
  };
  const assignActions = () => {
    return fields.map((fieldName, fieldIndex) => {
      // Fetch the content from the field Values
      const cd = (0, _cloneDeep.default)((0, _get.default)(values, fieldName));
      cd.actionListActions = actionAssigner(cd);
      return {
        ...cd,
        fieldName,
        fieldIndex
      };
    });
  };
  const {
    formatter,
    ...restOfMclProps
  } = mclProps; // Destructure formatter part of mclProps

  const fieldAwareFormatter = () => {
    const returnObj = {};
    // For each visible field, if it's being edited then ignore passed formatters, else use them

    visibleFields.forEach((key, index) => {
      // Work out now whether or not to autofocus, so that gets cemented into the returnObj function
      const shouldAutoFocus = index === 0;
      returnObj[key] = cd => {
        // Row is being edited if it has no id, or its id is in the editing string
        const editingRow = cd.id === editing || !cd.id;

        // Default to the passed formatter values
        let returnValue = formatter?.[key] ? formatter[key](cd) : cd[key];

        // If editing, replace values with fields
        if (editingRow) {
          /*
            Check if the property is a visible field.
            If it is not then we don't allow editing in this component.
          */
          const editFunction = editableFields[key] ?? (() => true);
          const createFunction = creatableFields[key] ?? (() => true);
          /*
            Next check if this is a new row, if so we should run the createableField function with the data.
            If it is not a new row, then we run the editableField function with the data,
            and it should return true/false
             For both checks
            true => Field, false => display value
          */
          if (!cd.id && createFunction(cd) || !!cd.id && editFunction(cd)) {
            const passedObject = {
              allValues: values,
              index: cd?.fieldIndex,
              key,
              name: `${cd.fieldName}.${key}`,
              rowFieldName: cd.fieldName
            };
            const validateFunction = validateFields?.[key] ? validateFields?.[key](passedObject) : null;
            returnValue = fieldComponents[key] ? fieldComponents[key](passedObject) : /*#__PURE__*/(0, _jsxRuntime.jsx)(_reactFinalForm.Field, {
              ariaLabel: key // TODO at the moment the only way to override this is passing in an entire fieldComponent.
              ,
              autoFocus: shouldAutoFocus // TODO at the moment this will always be the first field, even if it's not editable
              ,
              component: _components.TextField,
              marginBottom0: true,
              name: `${cd.fieldName}.${key}`,
              parse: v => v,
              validate: validateFunction
            });
          }
        }
        return returnValue;
      };
    });
    return returnObj;
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
    children: [/*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: _ActionListFieldArray.default.header,
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Headline, {
        className: _ActionListFieldArray.default.headerText,
        children: label
      }), !hideCreateButton && /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Button, {
        disabled: !!editing || !createCallback,
        marginBottom0: true,
        onClick: handleClickCreate,
        children: kintIntl.formatKintMessage({
          id: 'new',
          overrideValue: labelOverrides?.new
        })
      })]
    }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.MultiColumnList, {
      columnMapping: {
        ...columnMapping,
        actionListActions: kintIntl.formatKintMessage({
          id: 'actions',
          overrideValue: labelOverrides?.actions
        })
      },
      columnWidths: getColumnWidths(),
      contentData: assignActions(),
      formatter: {
        actionListActions: renderActionButtons,
        ...fieldAwareFormatter()
      },
      interactive: !!onRowClick,
      onRowClick: onRowClick ? (e, row) => {
        // Make sure we ONLY fire row click from row, and not from action buttons.
        const targetIsAction = e.target.closest('[id^=action-button-parent]') !== null;
        if (!targetIsAction) {
          const {
            actionListActions: _ala,
            ...rowWithoutActions
          } = row;
          onRowClick(e, rowWithoutActions);
        }
      } : null,
      visibleColumns: !hideActionsColumn || !!editing ? [...visibleFields, 'actionListActions'] : visibleFields,
      ...restOfMclProps
    })]
  });
});
ActionListFieldArray.propTypes = propTypes;
var _default = exports.default = ActionListFieldArray;